import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, Banknote, CheckCircle2, TrendingDown, Wallet } from 'lucide-react';
import {
  addMonths,
  endOfMonth,
  isDateInRange,
  isDateInSelectedPeriod,
  MONTH_LABELS,
  parseDateSafe,
  resolveChartAxisColumns,
  startOfMonth,
  startOfYear,
} from '../../../placeholders/periodSummaryUtils';
import { money } from '../treasury/treasuryUi';

const CHART_BODY_HEIGHT = 260;
const PAD = { top: 16, right: 18, bottom: 34, left: 64 };

const CARD_TONES = {
  dark: {
    light: { bg: '#0f172a', color: '#f8fafc', muted: '#94a3b8', deltaUp: '#4ade80', deltaDown: '#fb7185' },
    dark: { bg: '#020617', color: '#f8fafc', muted: '#94a3b8', deltaUp: '#4ade80', deltaDown: '#fb7185' },
  },
  mint: {
    light: { bg: '#d1fae5', color: '#064e3b', muted: '#047857', deltaUp: '#047857', deltaDown: '#b91c1c' },
    dark: { bg: '#064e3b', color: '#d1fae5', muted: '#6ee7b7', deltaUp: '#86efac', deltaDown: '#fca5a5' },
  },
  rose: {
    light: { bg: '#fce7f3', color: '#9d174d', muted: '#be185d', deltaUp: '#047857', deltaDown: '#9d174d' },
    dark: { bg: '#831843', color: '#fce7f3', muted: '#f9a8d4', deltaUp: '#86efac', deltaDown: '#fda4af' },
  },
  sky: {
    light: { bg: '#e0f2fe', color: '#0c4a6e', muted: '#0369a1', deltaUp: '#0369a1', deltaDown: '#b91c1c' },
    dark: { bg: '#0c4a6e', color: '#e0f2fe', muted: '#7dd3fc', deltaUp: '#7dd3fc', deltaDown: '#fca5a5' },
  },
};

const formatCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toLocaleString('es-CL', { maximumFractionDigits: 1 })} mil MM`;
  if (abs >= 1e6) return `$${(n / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })} MM`;
  if (abs >= 1e5) return `$${(n / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })} mil`;
  return money(n);
};

const formatAxis = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })}k`;
  return `${sign}$${Math.round(abs).toLocaleString('es-CL')}`;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const endOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
const addDays = (date, amount) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + amount);

const formatDayLabel = (date) => `${date.getDate()} ${MONTH_LABELS[date.getMonth()]} ${date.getFullYear()}`;

const formatWeekLabel = (start, end) => {
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} ${MONTH_LABELS[start.getMonth()]} ${start.getFullYear()}`;
  }
  if (sameYear) {
    return `${start.getDate()} ${MONTH_LABELS[start.getMonth()]} – ${end.getDate()} ${MONTH_LABELS[end.getMonth()]} ${start.getFullYear()}`;
  }
  return `${formatDayLabel(start)} – ${formatDayLabel(end)}`;
};

const buildDailyColumns = (start, end) => {
  const columns = [];
  let cursor = startOfDay(start);
  const last = startOfDay(end);
  while (cursor <= last) {
    columns.push({
      key: `d-${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      start: startOfDay(cursor),
      end: endOfDay(cursor),
      label: formatDayLabel(cursor),
    });
    cursor = addDays(cursor, 1);
  }
  return columns;
};

const buildWeeklyColumns = (start, end) => {
  const columns = [];
  let cursor = startOfDay(start);
  const rangeEnd = end;
  while (cursor <= rangeEnd) {
    const weekEnd = endOfDay(addDays(cursor, 6));
    const clippedEnd = weekEnd > rangeEnd ? rangeEnd : weekEnd;
    columns.push({
      key: `w-${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`,
      start: cursor,
      end: clippedEnd,
      label: formatWeekLabel(cursor, clippedEnd),
    });
    cursor = addDays(cursor, 7);
  }
  return columns;
};

const resolvePlotColumns = (selectedPeriod) => {
  const months = selectedPeriod?.columns || [];
  const isMonthMode = !selectedPeriod?.mode || selectedPeriod.mode === 'month';
  if (!isMonthMode || months.length > 12 || months.length === 0) return months;
  const start = months[0].start;
  const end = months[months.length - 1].end;
  if (!start || !end) return months;
  if (months.length <= 2) return buildDailyColumns(start, end);
  return buildWeeklyColumns(start, end);
};

const pctChange = (current, previous) => {
  if (previous == null || !Number.isFinite(Number(previous))) return null;
  const prev = Number(previous);
  const curr = Number(current || 0);
  if (Math.abs(prev) < 0.5) return Math.abs(curr) < 0.5 ? 0 : 100;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

const previousRangeFor = (selectedPeriod, periodFilter) => {
  const range = selectedPeriod?.range;
  if (!range?.start || !range?.end || periodFilter === 'all') return null;
  const start = range.start;
  const end = range.end;
  if (periodFilter === 'current_month' || periodFilter === 'previous_month' || periodFilter === 'monthly') {
    const prevStart = addMonths(startOfMonth(start), -1);
    return { start: prevStart, end: endOfMonth(prevStart) };
  }
  if (periodFilter === 'ytd' || periodFilter === 'annual_ytd' || periodFilter === 'annual') {
    const year = start.getFullYear() - 1;
    return { start: startOfYear(year), end: new Date(year, end.getMonth(), end.getDate(), 23, 59, 59, 999) };
  }
  const ms = Math.max(end.getTime() - start.getTime(), 0);
  return { start: new Date(start.getTime() - ms - 1), end: new Date(start.getTime() - 1) };
};

const summarize = (rows) =>
  rows.reduce(
    (acc, row) => {
      acc.income += Number(row.income || 0);
      acc.expense += Number(row.expense || 0);
      acc.count += 1;
      acc.extraAmount += Number(row.extraAmount || 0);
      acc.extraCount += Number(row.extraCount || 0);
      return acc;
    },
    { income: 0, expense: 0, count: 0, extraAmount: 0, extraCount: 0 }
  );

const flowDelta = (rowOrStats, subtractNegatives) => {
  const income = Number(rowOrStats.income || 0);
  const expense = Number(rowOrStats.expense || 0);
  return subtractNegatives ? income - expense : income;
};

const Delta = ({ value, invert = false, tone }) => {
  if (value == null || !Number.isFinite(value)) {
    return <span style={{ fontSize: 11, color: tone.muted }}>Sin periodo previo</span>;
  }
  const up = value >= 0;
  const good = invert ? !up : up;
  const color = good ? tone.deltaUp : tone.deltaDown;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11, fontWeight: 700, color }}>
      <Icon size={13} />
      {`${up ? '+' : ''}${value.toLocaleString('es-CL', { maximumFractionDigits: 1 })}% vs periodo anterior`}
    </span>
  );
};

const MetricCard = ({ toneKey, isDarkMode, icon: Icon, label, value, hint, delta, invertDelta = false }) => {
  const tone = CARD_TONES[toneKey][isDarkMode ? 'dark' : 'light'];
  return (
    <div
      style={{
        background: tone.bg,
        color: tone.color,
        borderRadius: 18,
        padding: '16px 18px',
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: tone.muted }}>
          {label}
        </span>
        {Icon ? <Icon size={16} color={tone.muted} /> : null}
      </div>
      <div style={{ marginTop: 10, fontSize: 26, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em' }}>{value}</div>
      {hint ? <div style={{ marginTop: 4, fontSize: 11, color: tone.muted }}>{hint}</div> : null}
      <div style={{ marginTop: 8 }}>
        <Delta value={delta} invert={invertDelta} tone={tone} />
      </div>
    </div>
  );
};

const xFromTime = (value, t0, spanT, plotW) => {
  const t = value instanceof Date ? value.getTime() : Number(value);
  if (!Number.isFinite(t) || spanT <= 0) return PAD.left;
  const x = PAD.left + (plotW * (t - t0)) / spanT;
  return Math.min(PAD.left + plotW, Math.max(PAD.left, x));
};

const BalanceChart = ({ points, axisTicks = null, xDomain = null, ui, isDarkMode, hoverPositiveLabel, hoverNegativeLabel }) => {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const apply = () => {
      const next = {
        width: Math.max(1, Math.floor(node.clientWidth)),
        height: Math.max(1, Math.floor(node.clientHeight)),
      };
      setSize((prev) => (prev.width === next.width && prev.height === next.height ? prev : next));
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const width = size.width;
  const height = size.height;
  const plotW = Math.max(width - PAD.left - PAD.right, 1);
  const plotH = Math.max(height - PAD.top - PAD.bottom, 1);
  const values = points.map((point) => Number(point.balance || 0));
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = Math.max(max - min, 1);
  const times = points.map((point) => (point.at instanceof Date ? point.at.getTime() : null));
  const domainStart = xDomain?.start instanceof Date ? xDomain.start.getTime() : times.find((t) => t != null);
  const domainEnd = xDomain?.end instanceof Date ? xDomain.end.getTime() : [...times].reverse().find((t) => t != null);
  const hasTime = Number.isFinite(domainStart) && Number.isFinite(domainEnd) && domainEnd !== domainStart;
  const t0 = hasTime ? domainStart : 0;
  const spanT = hasTime ? domainEnd - domainStart : 1;
  const last = Math.max(points.length - 1, 1);
  const coords = points.map((point, index) => {
    const x = hasTime
      ? xFromTime(times[index] ?? domainStart, t0, spanT, plotW)
      : PAD.left + (plotW * index) / last;
    const y = PAD.top + ((max - Number(point.balance || 0)) / span) * plotH;
    return { ...point, x, y };
  });
  const line = coords.map((point) => `${point.x},${point.y}`).join(' ');
  const area = coords.length
    ? `${coords[0].x},${PAD.top + plotH} ${line} ${coords[coords.length - 1].x},${PAD.top + plotH}`
    : '';
  const zeroY = PAD.top + ((max - 0) / span) * plotH;
  const ticks = 4;
  const labelStep = coords.length > 20 ? 3 : coords.length > 12 ? 2 : 1;
  const xLabels = (() => {
    if (axisTicks?.length) {
      const mapped = axisTicks
        .map((tick) => {
          if (hasTime && tick.at instanceof Date) {
            return { key: tick.key, x: xFromTime(tick.at, t0, spanT, plotW), label: tick.label };
          }
          const point = coords[tick.index];
          if (!point) return null;
          return { key: tick.key, x: point.x, label: tick.label };
        })
        .filter(Boolean);
      const step = mapped.length > 16 ? 2 : 1;
      return mapped.filter((tick, index) => index % step === 0 || index === mapped.length - 1);
    }
    return coords
      .filter((_, index) => index % labelStep === 0 || index === coords.length - 1)
      .map((point) => ({ key: point.key, x: point.x, label: point.shortLabel || point.label }));
  })();
  const lineColor = '#0d9488';
  const fill = isDarkMode ? 'rgba(45, 212, 191, 0.22)' : 'rgba(13, 148, 136, 0.18)';
  const grid = isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.35)';

  const onMove = (event) => {
    if (!coords.length) return;
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = coords[0];
    let best = Math.abs(coords[0].x - x);
    coords.forEach((point) => {
      const dist = Math.abs(point.x - x);
      if (dist < best) {
        best = dist;
        nearest = point;
      }
    });
    setHover(nearest);
  };

  if (!width || !height) {
    return <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }} />;
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', overflow: 'visible' }}
      >
        {Array.from({ length: ticks + 1 }).map((_, index) => {
          const value = max - (span * index) / ticks;
          const y = PAD.top + (plotH * index) / ticks;
          return (
            <g key={`tick-${index}`}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y} y2={y} stroke={grid} strokeWidth="1" />
              <text x={PAD.left - 8} y={y + 3} textAnchor="end" fill={ui.muted} fontSize="11">
                {formatAxis(value)}
              </text>
            </g>
          );
        })}
        {min < 0 && max > 0 ? (
          <line x1={PAD.left} x2={width - PAD.right} y1={zeroY} y2={zeroY} stroke={ui.border} strokeDasharray="4 4" />
        ) : null}
        {coords.length ? (
          <>
            <polygon points={area} fill={fill} />
            <polyline
              points={line}
              fill="none"
              stroke={lineColor}
              strokeWidth={coords.length > 40 ? 2 : 2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {coords.map((point) => (
              <circle
                key={point.key}
                cx={point.x}
                cy={point.y}
                r={hover?.key === point.key ? 5 : 0}
                fill="#fff"
                stroke={lineColor}
                strokeWidth="2"
              />
            ))}
          </>
        ) : null}
        {xLabels.map((tick) => (
          <text key={`label-${tick.key}`} x={tick.x} y={height - 6} textAnchor="middle" fill={ui.muted} fontSize="11">
            {tick.label}
          </text>
        ))}
      </svg>
      {hover ? (
        <div
          style={{
            position: 'absolute',
            left: `${Math.min(82, Math.max(8, (hover.x / width) * 100))}%`,
            top: 8,
            transform: 'translateX(-50%)',
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            padding: '8px 10px',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            pointerEvents: 'none',
            minWidth: 160,
            zIndex: 2,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted }}>{hover.label}</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: ui.text, marginTop: 2 }}>{money(hover.balance)}</div>
          <div style={{ fontSize: 11, color: ui.muted, marginTop: 4 }}>
            {hoverPositiveLabel} {money(hover.income)} · {hoverNegativeLabel} {money(hover.expense)}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PeriodFlowOverview = ({
  flowRows = [],
  openingBalance = 0,
  resetAtPeriodStart = false,
  subtractNegatives = true,
  selectedPeriod = null,
  chartColumns = null,
  periodFilter = 'last_36',
  periodTitle = '',
  lineTitle = 'Total',
  hoverPositiveLabel = 'Ingresos',
  hoverNegativeLabel = 'Egresos',
  totalLabel = 'Total',
  positiveLabel = 'Ingresos',
  negativeLabel = 'Egresos',
  extraLabel = 'Extra',
  extraIcon: ExtraIcon = CheckCircle2,
  extraMetric = 'extraAmount',
  extraHint,
  footnote = '',
  isDarkMode = false,
  ui,
  loading = false,
  children = null,
}) => {
  const detailColumns = selectedPeriod?.columns || [];
  const plotColumns = useMemo(() => resolvePlotColumns(selectedPeriod), [selectedPeriod]);
  const axisColumns = useMemo(
    () => (chartColumns?.length ? chartColumns : resolveChartAxisColumns(selectedPeriod)),
    [chartColumns, selectedPeriod]
  );
  const xDomain = useMemo(() => {
    if (!plotColumns.length) return null;
    return { start: plotColumns[0].start, end: plotColumns[plotColumns.length - 1].end };
  }, [plotColumns]);

  const series = useMemo(() => {
    const openingCutoff = plotColumns[0]?.start || selectedPeriod?.range?.start;
    const before = resetAtPeriodStart
      ? []
      : flowRows.filter((row) => {
          const date = parseDateSafe(row.date);
          return date && openingCutoff && date < openingCutoff;
        });
    let running = resetAtPeriodStart
      ? 0
      : openingBalance + before.reduce((sum, row) => sum + flowDelta(row, subtractNegatives), 0);
    const points = plotColumns.map((column) => {
      const inColumn = flowRows.filter((row) => {
        const date = parseDateSafe(row.date);
        return date && date >= column.start && date <= column.end;
      });
      const stats = summarize(inColumn);
      running += flowDelta(stats, subtractNegatives);
      const label = String(column.label || '');
      const monthToken = label.split(' ')[0];
      return {
        key: column.key,
        label,
        shortLabel: plotColumns.length > 14 && monthToken !== 'Ene' ? monthToken : label,
        at: column.end,
        income: stats.income,
        expense: stats.expense,
        count: stats.count,
        balance: running,
      };
    });
    if (resetAtPeriodStart && points.length && plotColumns[0]?.start) {
      return [
        {
          key: 'period-start',
          label: points[0].label,
          shortLabel: '',
          at: plotColumns[0].start,
          income: 0,
          expense: 0,
          count: 0,
          balance: 0,
        },
        ...points,
      ];
    }
    return points;
  }, [flowRows, plotColumns, openingBalance, resetAtPeriodStart, selectedPeriod, subtractNegatives]);

  const axisTicks = useMemo(() => {
    if (!plotColumns.length) return [];
    const monthAxis = detailColumns;
    const fineGrain = monthAxis.length > 0 && plotColumns.length > monthAxis.length;
    const buckets = fineGrain ? monthAxis : axisColumns;
    const sameGrain =
      !fineGrain && buckets.length === plotColumns.length && buckets[0]?.key === plotColumns[0]?.key;
    if (sameGrain) return [];
    return buckets
      .map((bucket) => {
        const matches = plotColumns
          .map((column, index) => (column.end >= bucket.start && column.start <= bucket.end ? index : -1))
          .filter((index) => index >= 0);
        if (!matches.length) return null;
        return {
          index: matches[0],
          key: bucket.key,
          label: bucket.label,
          at: bucket.start,
        };
      })
      .filter(Boolean);
  }, [axisColumns, detailColumns, plotColumns]);

  const periodRows = useMemo(
    () => flowRows.filter((row) => isDateInSelectedPeriod(row.date, selectedPeriod)),
    [flowRows, selectedPeriod]
  );
  const current = useMemo(() => summarize(periodRows), [periodRows]);
  const periodTotal = flowDelta(current, subtractNegatives);
  const endingBalance = resetAtPeriodStart
    ? periodTotal
    : series.length
      ? series[series.length - 1].balance
      : openingBalance;

  const previous = useMemo(() => {
    const range = previousRangeFor(selectedPeriod, periodFilter);
    if (!range) return null;
    const rows = flowRows.filter((row) => isDateInRange(row.date, range));
    const stats = summarize(rows);
    if (resetAtPeriodStart) {
      return { ...stats, balance: flowDelta(stats, subtractNegatives) };
    }
    const beforeEnd = flowRows.filter((row) => {
      const date = parseDateSafe(row.date);
      return date && date <= range.end;
    });
    const balance =
      openingBalance + beforeEnd.reduce((sum, row) => sum + flowDelta(row, subtractNegatives), 0);
    return { ...stats, balance };
  }, [flowRows, openingBalance, periodFilter, resetAtPeriodStart, selectedPeriod, subtractNegatives]);

  const extraValue = extraMetric === 'count' ? current.count : current.extraAmount;
  const previousExtra = extraMetric === 'count' ? previous?.count : previous?.extraAmount;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
        }}
      >
        <MetricCard
          toneKey="dark"
          isDarkMode={isDarkMode}
          icon={Wallet}
          label={totalLabel}
          value={loading ? '…' : formatCompact(endingBalance)}
          delta={pctChange(endingBalance, previous?.balance)}
        />
        <MetricCard
          toneKey="mint"
          isDarkMode={isDarkMode}
          icon={Banknote}
          label={positiveLabel}
          value={loading ? '…' : formatCompact(current.income)}
          delta={pctChange(current.income, previous?.income)}
        />
        <MetricCard
          toneKey="rose"
          isDarkMode={isDarkMode}
          icon={TrendingDown}
          label={negativeLabel}
          value={loading ? '…' : formatCompact(current.expense)}
          delta={pctChange(current.expense, previous?.expense)}
          invertDelta
        />
        <MetricCard
          toneKey="sky"
          isDarkMode={isDarkMode}
          icon={ExtraIcon}
          label={extraLabel}
          value={
            loading
              ? '…'
              : extraMetric === 'count'
                ? extraValue.toLocaleString('es-CL')
                : formatCompact(extraValue)
          }
          hint={loading || typeof extraHint !== 'function' ? '' : extraHint(current)}
          delta={pctChange(extraValue, previousExtra)}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: children ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)',
          gap: 12,
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            background: ui.panel,
            border: `1px solid ${ui.border}`,
            borderRadius: 10,
            minWidth: 0,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: '7px 10px',
              borderBottom: `1px solid ${ui.border}`,
              background: ui.header,
              flexShrink: 0,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: ui.text, lineHeight: 1.2 }}>{lineTitle}</h3>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: ui.muted }}>
                {periodTitle || 'Línea continua'}
                {footnote ? ` · ${footnote}` : ''}
              </p>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: ui.text, whiteSpace: 'nowrap' }}>
              {loading ? '…' : money(endingBalance)}
            </div>
          </div>
          {loading ? (
            <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>Calculando…</p>
          ) : series.length ? (
            <div style={{ flex: 1, minHeight: CHART_BODY_HEIGHT, position: 'relative', padding: '8px 12px 10px' }}>
              <BalanceChart
                points={series}
                axisTicks={axisTicks}
                xDomain={xDomain}
                ui={ui}
                isDarkMode={isDarkMode}
                hoverPositiveLabel={hoverPositiveLabel}
                hoverNegativeLabel={hoverNegativeLabel}
              />
            </div>
          ) : (
            <p style={{ margin: 0, padding: '24px 12px', fontSize: 13, color: ui.muted }}>
              No hay movimientos en el período seleccionado.
            </p>
          )}
        </div>
        {children ? (
          <div style={{ minWidth: 0, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PeriodFlowOverview;
